import { formatDate } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { isValid, parseISO } from "date-fns";

import { PLACEHOLDER, UNKNOWN_USER, USER_TYPE_LABELS } from "./constants";
import { ProfileChangeLog, UPDATE_TYPES } from "@clipboard-health/contract-backend-main";

export interface UpdatedField {
  key: string;
  previousValue: unknown;
  newValue: unknown;
}

export interface TableRowData {
  id: string;
  datetime: Date;
  performedBy: string;
  updateType: string;
  action: string;
  context: string;
  updatedFields: UpdatedField[];
}

export function formatContext(log: ProfileChangeLog): string {
  if (isDefined(log.document?.name)) {
    return log.document.name;
  }

  if (isDefined(log.requirement?.name)) {
    return log.requirement.name;
  }

  if (isDefined(log.user)) {
    const userTypeLabel = isDefined(log.user.type) ? USER_TYPE_LABELS[log.user.type] : undefined;
    const label = userTypeLabel ?? log.user.type ?? "User";
    const email = log.user.email ?? UNKNOWN_USER;
    return `${label}: ${email}`;
  }

  return PLACEHOLDER;
}

export function formatExpiryValue(value: unknown): unknown {
  if (typeof value !== "string") {
    return value;
  }
  const parsed = parseISO(value);
  return isValid(parsed) ? formatDate(parsed) : value;
}

export function transformLogsToTableRows(logs: ProfileChangeLog[]): TableRowData[] {
  return logs.map((log) => ({
    id: log._id,
    datetime: log.createdAt,
    performedBy: log.performedBy.email ?? UNKNOWN_USER,
    updateType: log.updateType,
    action: log.action,
    context: formatContext(log),
    updatedFields: transformUpdatedFields(log),
  }));
}

function transformUpdatedFields(log: ProfileChangeLog): UpdatedField[] {
  const { updatedFields } = log;
  return updatedFields.map((updatedField) => {
    const key = updatedField.key ?? "Unknown";

    const isExpiryUpdate =
      key === "expiry" &&
      (log.updateType === UPDATE_TYPES.DOC || log.updateType === UPDATE_TYPES.REQUIREMENT);

    return {
      key,
      ...(isExpiryUpdate
        ? {
            previousValue: formatExpiryValue(updatedField.previousValue),
            newValue: formatExpiryValue(updatedField.newValue),
          }
        : { previousValue: updatedField.previousValue, newValue: updatedField.newValue }),
    };
  });
}
